import { MoneyMessage } from '../../../types/MoneyMessage';
import { TableBuilder } from '../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../cdl/TableBuilder/TableBuilderColumn';
import { Tooltip } from '../../cdl/Tooltip/Tooltip';
import { PriceHeading } from '../FormikProductTable/common/PriceHeading';
import { formatMoney } from '../helpers/formatMoney.helper';
import { formatPrice } from '../helpers/formatPrice.helper';
import { translate } from '../helpers/translate.helper';
import { Characters } from '../helpers/unicode.helper';
import { IconReplace } from '../icons/cdl/Replace';
import { IconX } from '../icons/cdl/X';
import { Box } from '../ui/Box';

export interface PriceBreakdownItem {
    isTranslated?: boolean;
    isTranslationSuccessful?: boolean;
    productName?: string;
    baseProductName?: string;
    unitPrice?: MoneyMessage;
    totalPrice?: MoneyMessage;
}

interface PriceItemsBreakdownProps {
    items: PriceBreakdownItem[];
}

export const PriceItemsBreakdown = ({ items }: PriceItemsBreakdownProps) => {
    if (!items.length) {
        return null;
    }

    return (
        <TableBuilder<PriceBreakdownItem> variant="inline" data={items}>
            <TableBuilderColumn<PriceBreakdownItem> header={translate('order.packagePrices.product')}>
                {(row: PriceBreakdownItem) => {
                    const productNameOrDefault = row.productName || translate('global.notavailable');

                    if (row.isTranslated) {
                        return (
                            <Tooltip
                                label={translate('order.equivalentTooltip', {
                                    productName: row.baseProductName ?? translate('global.notavailable'),
                                })}
                                showArrow
                            >
                                <Box display="flex" alignItems="center" gap={8}>
                                    <IconReplace width={16} height={16} />
                                    <span>{productNameOrDefault}</span>
                                </Box>
                            </Tooltip>
                        );
                    }

                    if (row.isTranslationSuccessful === false) {
                        return (
                            <Box display="flex" alignItems="center" gap={8}>
                                <IconX width={16} height={16} />
                                <span>{productNameOrDefault}</span>
                            </Box>
                        );
                    }

                    return productNameOrDefault;
                }}
            </TableBuilderColumn>
            <TableBuilderColumn<PriceBreakdownItem> header={<PriceHeading />} numeric>
                {(row: PriceBreakdownItem) => (row.unitPrice ? formatPrice(row.unitPrice) : Characters.EN_DASH)}
            </TableBuilderColumn>
            <TableBuilderColumn<PriceBreakdownItem> header={translate('order.packagePrices.total')} numeric>
                {(row: PriceBreakdownItem) => {
                    if (!row.totalPrice) {
                        return Characters.EN_DASH;
                    }

                    return formatMoney({
                        ...row.totalPrice,
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    });
                }}
            </TableBuilderColumn>
        </TableBuilder>
    );
};
